/**
 * Vendi a noi (sections/sell-to-us.liquid)
 *
 * - Hero (photo background): slides crossfade with a slow zoom every few seconds while the
 *   hero is on screen, and --stu-hero-p (0 → 1 as the hero scrolls away) drives the parallax.
 * - Reveal: elements with [data-reveal] fade and rise in once as they enter the viewport.
 * - Steps: the rail linking the numbers is drawn as the list scrolls past the middle of the
 *   screen, and each number lights up when the line reaches it.
 * - Sticky WhatsApp button (phones, CSS-gated): visible once the hero has scrolled away and
 *   hidden again from the final call to action onwards, so it never doubles the CTA button.
 * - Shelf scanner: the "Perché vendere a noi" photo pans inside its frame as the page scrolls,
 *   a scan line sweeps it, and each hotspot lights up once the line has passed over it.
 * - Benefits: the item crossing the middle of the screen becomes "active" (coloured icon and
 *   its own small animation), so on a phone they light up one after another.
 * - prefers-reduced-motion: everything is shown immediately and the rail is fully drawn.
 */

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

class SellToUs extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #revealObserver;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {IntersectionObserver | undefined} */
  #benefitObserver;
  /** @type {IntersectionObserver | undefined} */
  #heroObserver;
  #slideTimer = 0;
  #slideIndex = 0;
  #heroVisible = true;
  #frame = 0;
  /** Centres of the step numbers, in px from the top of the steps list (transform-free). */
  /** @type {number[]} */
  #numberCentres = [];

  connectedCallback() {
    if (reducedMotion.matches) {
      this.classList.remove('stu--js');
    } else {
      this.classList.add('stu--js');
      this.#observeReveals();
    }

    this.#resizeObserver = new ResizeObserver(() => {
      this.#measureRail();
      this.#update();
    });
    this.#resizeObserver.observe(this);

    this.#observeBenefits();
    this.#startHeroSlides();

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#measureRail();
    this.#update();
  }

  disconnectedCallback() {
    this.#revealObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#benefitObserver?.disconnect();
    this.#heroObserver?.disconnect();
    clearInterval(this.#slideTimer);
    window.removeEventListener('scroll', this.#onScroll);
    cancelAnimationFrame(this.#frame);
  }

  #observeReveals() {
    this.#revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Also reveal anything already scrolled past (restored scroll position, fast flicks)
          const passed = entry.boundingClientRect.bottom < 0;
          if (!entry.isIntersecting && !passed) continue;
          entry.target.classList.add('is-in');
          this.#revealObserver?.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    for (const element of this.querySelectorAll('[data-reveal]')) this.#revealObserver.observe(element);
  }

  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    this.#updateHero();
    this.#updateSteps();
    this.#updateSticky();
    this.#updateScanner();
  }

  /* ---------------------------------------------------------------- hero */

  #startHeroSlides() {
    const slides = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-hero-slide]')]);
    const first = slides[0];
    if (!first) return;

    // Kick off the slow zoom of the first photo on the next frame
    requestAnimationFrame(() => first.classList.add('is-current'));
    if (slides.length < 2 || reducedMotion.matches) return;

    const hero = this.querySelector('[data-hero]');
    if (hero) {
      this.#heroObserver = new IntersectionObserver(([entry]) => {
        this.#heroVisible = Boolean(entry?.isIntersecting);
      });
      this.#heroObserver.observe(hero);
    }

    this.#slideTimer = window.setInterval(() => {
      if (!this.#heroVisible || document.hidden) return;
      slides[this.#slideIndex]?.classList.remove('is-current');
      this.#slideIndex = (this.#slideIndex + 1) % slides.length;
      slides[this.#slideIndex]?.classList.add('is-current');
    }, 5500);
  }

  #updateHero() {
    const hero = this.querySelector('[data-hero]');
    if (!(hero instanceof HTMLElement) || !hero.classList.contains('stu-hero--bg')) return;
    if (reducedMotion.matches) return;
    const rect = hero.getBoundingClientRect();
    const progress = Math.min(Math.max(-rect.top / Math.max(rect.height, 1), 0), 1);
    hero.style.setProperty('--stu-hero-p', progress.toFixed(4));
  }

  /* ---------------------------------------------------------------- benefits */

  /** Active = crossing a band around the middle of the screen. */
  #observeBenefits() {
    const items = this.querySelectorAll('[data-benefit]');
    if (items.length === 0) return;
    this.#benefitObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) entry.target.classList.toggle('is-active', entry.isIntersecting);
      },
      { rootMargin: '-38% 0px -38% 0px' }
    );
    for (const item of items) this.#benefitObserver.observe(item);
  }

  /* ---------------------------------------------------------------- shelf scanner */

  #updateScanner() {
    const scanner = this.querySelector('[data-scanner]');
    const stage = scanner?.querySelector('[data-scanner-stage]');
    if (!(scanner instanceof HTMLElement) || !(stage instanceof HTMLElement)) return;

    const spots = /** @type {HTMLElement[]} */ ([...stage.querySelectorAll('[data-spot]')]);
    const frame = scanner.getBoundingClientRect();
    const overflow = Math.max(stage.offsetHeight - scanner.offsetHeight, 0);
    const viewport = window.innerHeight;

    if (reducedMotion.matches) {
      // Static: centre the photo and show every label
      stage.style.setProperty('--stu-pan', `${(-overflow / 2).toFixed(1)}px`);
      for (const spot of spots) spot.classList.add('is-on');
      return;
    }

    // Starts as the photo enters the lower part of the screen, ends as it leaves the top third
    const start = viewport * 0.9;
    const end = viewport * 0.3;
    let progress = (start - frame.top) / Math.max(start - end + frame.height - viewport * 0.25, 1);
    progress = Math.min(Math.max(progress, 0), 1);

    // Pan the shelf top → bottom; the line therefore travels top → bottom of the frame
    stage.style.setProperty('--stu-pan', `${(-progress * overflow).toFixed(1)}px`);
    stage.style.setProperty('--stu-scan', progress.toFixed(4));
    stage.style.setProperty('--stu-scan-visible', progress > 0.01 && progress < 0.99 ? '1' : '0');

    const scanned = progress * 100;
    for (const spot of spots) {
      const top = Number(spot.dataset.top) || 0;
      const height = Number(spot.dataset.height) || 0;
      spot.classList.toggle('is-on', scanned >= top + height * 0.35);
    }
  }

  /* ---------------------------------------------------------------- steps */

  /**
   * Position the rail from the centre of the first number to the centre of the last.
   * Uses offsetTop so the steps' own reveal transform doesn't skew the measurement.
   */
  #measureRail() {
    const steps = this.querySelector('[data-steps]');
    if (!(steps instanceof HTMLElement)) return;

    this.#numberCentres = [...steps.querySelectorAll('[data-step]')].map((item) => {
      const number = item.querySelector('[data-step-num]');
      if (!(item instanceof HTMLElement) || !(number instanceof HTMLElement)) return 0;
      return item.offsetTop + number.offsetTop + number.offsetHeight / 2;
    });

    const start = this.#numberCentres[0];
    const end = this.#numberCentres[this.#numberCentres.length - 1];
    if (start === undefined || end === undefined || this.#numberCentres.length < 2) {
      steps.style.setProperty('--stu-rail-height', '0px');
      return;
    }
    steps.style.setProperty('--stu-rail-top', `${start}px`);
    steps.style.setProperty('--stu-rail-height', `${Math.max(end - start, 0)}px`);
  }

  #updateSteps() {
    const steps = this.querySelector('[data-steps]');
    if (!(steps instanceof HTMLElement)) return;
    const items = steps.querySelectorAll('[data-step]');

    if (reducedMotion.matches) {
      steps.style.setProperty('--stu-progress', '1');
      for (const item of items) item.classList.add('is-active');
      return;
    }

    const top = steps.getBoundingClientRect().top;
    const start = this.#numberCentres[0] ?? 0;
    const end = this.#numberCentres[this.#numberCentres.length - 1] ?? 0;
    // The line is drawn up to a point a little below the middle of the screen
    const drawTo = window.innerHeight * 0.62 - top;
    const length = end - start;
    const progress = length > 0 ? (drawTo - start) / length : 1;
    steps.style.setProperty('--stu-progress', Math.min(Math.max(progress, 0), 1).toFixed(4));

    items.forEach((item, index) => {
      item.classList.toggle('is-active', (this.#numberCentres[index] ?? Infinity) <= drawTo + 1);
    });
  }

  /* ---------------------------------------------------------------- sticky button */

  #updateSticky() {
    const sticky = this.querySelector('[data-sticky]');
    if (!(sticky instanceof HTMLElement)) return;
    const hero = this.querySelector('[data-hero]');
    const cta = this.querySelector('[data-cta]');

    const pastHero = hero ? hero.getBoundingClientRect().bottom < 0 : true;
    const reachedCta = cta ? cta.getBoundingClientRect().top < window.innerHeight * 0.85 : false;
    const visible = pastHero && !reachedCta;

    if (visible === sticky.classList.contains('is-visible')) return;
    sticky.classList.toggle('is-visible', visible);
    const link = sticky.querySelector('a');
    if (link) link.tabIndex = visible ? 0 : -1;
  }
}

if (!customElements.get('sell-to-us')) {
  customElements.define('sell-to-us', SellToUs);
}
